import type { VorankuendigungData } from '../../types';
import { stabilerInhaltsHash } from '../../services/ablageHilfen';
import { berlinIsoTag, istKalenderTag, tagAusIso } from '../../services/vorank/vorankAnfragePruefung';
import {
    VORANK_EINLESEN_MAX_BYTES,
    vorankLinkUrl,
    type VorankDrehung,
    type VorankEinleseAuswahl,
    type VorankEinleseSeite,
    type VorankFassungAngelegt,
    type VorankFassungZeile,
    type VorankKartenStatus,
    type VorankPdfUnterschrift,
    type VorankUnterschrift,
} from '../../services/vorank/vorankUnterschriftTypes';
import type { VorankPlotStand } from '../sige-plan/plotAbrechnungen';

/**
 * „Vorankündigung unterschreiben“ — die reinen Regeln der Bearbeiter-Ansicht
 * (Plan docs/vorankuendigung-unterschrift-plan.md §5, §7, §8, §9).
 *
 * Hier steht nichts, was Supabase, jsPDF oder das DOM kennt: Plot-Quelle,
 * Texte der Statuskarte, Namen und Kennungen der Ablagen, der Zustand des
 * Einlese-Dialogs. Die Abläufe mit Wirkung stehen in
 * `vorankUnterschriftAblauf.ts`, die Oberfläche in `VorankUnterschriftKarte`,
 * `VorankEinlesenDialog` und `VorankuendigungView`.
 */

// ── Datum ───────────────────────────────────────────────────────────────

const zwei = (n: number): string => String(n).padStart(2, '0');

const alsDatum = (iso: string | null | undefined): Date | null => {
    const d = new Date(iso || '');
    return Number.isNaN(d.getTime()) ? null : d;
};

/** „TT.MM.JJJJ“ in Ortszeit — wie `datumKurz` der Ablage. Unlesbar → der Rohwert. */
export const vorankTag = (iso: string | null | undefined): string => {
    const d = alsDatum(iso);
    if (!d) return (iso || '').trim() || '—';
    return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** „TT.MM.JJJJ, HH:MM Uhr“ in Ortszeit. */
export const vorankTagZeit = (iso: string | null | undefined): string => {
    const d = alsDatum(iso);
    if (!d) return vorankTag(iso);
    return `${vorankTag(iso)}, ${zwei(d.getHours())}:${zwei(d.getMinutes())} Uhr`;
};

/** „JJJJ-MM-TT“ für Dateinamen — sortiert sich im Ordner von selbst. */
const datumFuerDatei = (iso: string | null | undefined): string => {
    const d = alsDatum(iso);
    if (!d) return 'ohne-Datum';
    return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
};

const zeitwert = (iso: string | null | undefined): number => alsDatum(iso)?.getTime() ?? 0;

/**
 * Wann unterschrieben wurde: „am 29.10.2026, 14:03 Uhr“ — bei einer digitalen
 * Unterschrift mit eingetragenem Datum (E6), das vom Tag des Eingangs
 * abweicht: „am 27.10.2026 (eingegangen 29.10.2026, 14:03 Uhr)“. Verglichen
 * wird mit dem BERLINER Tag des Eingangs — so rechnen RPC und PDF, nicht in
 * der Ortszeit des Geräts.
 */
export const vorankUnterschriebenAm = (u: Pick<VorankUnterschrift, 'weg' | 'am' | 'datum'> | null | undefined): string => {
    const eingegangen = vorankTagZeit(u?.am);
    const datum = u?.weg === 'digital' && istKalenderTag(u.datum) ? u.datum : '';
    if (!datum || datum === berlinIsoTag(u?.am)) return `am ${eingegangen}`;
    return `am ${tagAusIso(datum)} (eingegangen ${eingegangen})`;
};

/** Die jüngste Fassung (nach Anlage) — unabhängig davon, wie die Liste sortiert kam. */
const juengste = (liste: readonly VorankFassungZeile[]): VorankFassungZeile | null =>
    liste.reduce<VorankFassungZeile | null>(
        (beste, f) => (!beste || zeitwert(f.createdAt) > zeitwert(beste.createdAt) ? f : beste),
        null,
    );

/** Neueste zuerst, ohne die Eingabe zu verändern. */
const neuesteZuerst = (liste: readonly VorankFassungZeile[]): VorankFassungZeile[] =>
    [...liste].sort((a, b) => zeitwert(b.createdAt) - zeitwert(a.createdAt));

/** Läuft der Link der Fassung nicht mehr? Ohne lesbares Datum: nein (fail-open). */
export const vorankLinkAbgelaufen = (f: Pick<VorankFassungZeile, 'gueltigBis'>, jetzt: Date): boolean => {
    const bis = alsDatum(f.gueltigBis);
    return !!bis && bis.getTime() < jetzt.getTime();
};

// ── Unterschriebene PDF (Plan §7) ───────────────────────────────────────

/** Letzte 6 Zeichen des Tokens — die graue Nachweiszeile im PDF nennt sie. */
export const vorankKennung = (token: string | null | undefined): string => (token || '').trim().slice(-6);

/**
 * Die Unterschrift für `generateVorankuendigungPDF(inhalt, { unterschrift })`
 * — nur bei einer DIGITALEN Unterschrift mit Bild, sonst `null`.
 */
export const vorankPdfUnterschrift = (f: VorankFassungZeile): VorankPdfUnterschrift | null => {
    const u = f.unterschrift;
    if (!u || u.weg !== 'digital' || !(u.bild || '').trim()) return null;
    return {
        bild: u.bild as string,
        name: (u.name || '').trim(),
        funktion: u.funktion ?? null,
        am: u.am,
        // E6: das eingetragene Datum für „Ort, Datum“; die Nachweiszeile nimmt `am`.
        datum: u.datum ?? null,
        art: u.art ?? 'gezeichnet',
        kennung: vorankKennung(f.token),
        fassungVom: f.createdAt,
    };
};

/**
 * Woher die unterschriebene PDF einer Fassung kommt — oder `null`, wenn es
 * keine gibt. Digital mit Bild: aus Inhalt + Unterschrift erzeugt (ohne
 * Netz, immer gleich). Papier (oder digital ohne Bild, aber abgelegt): die
 * Datei aus „Dokumente“.
 */
export type VorankUnterschriebenQuelle =
    | { art: 'digital'; unterschrift: VorankPdfUnterschrift }
    | { art: 'dokument'; dokumentId: string };

export const vorankUnterschriebenQuelle = (f: VorankFassungZeile): VorankUnterschriebenQuelle | null => {
    if (!f.unterschrift) return null;
    const digital = vorankPdfUnterschrift(f);
    if (digital) return { art: 'digital', unterschrift: digital };
    const dokumentId = (f.signiertDokumentId || '').trim();
    return dokumentId ? { art: 'dokument', dokumentId } : null;
};

/** Dateiname des Downloads für die Behörde. */
export const vorankUnterschriebenDateiname = (f: VorankFassungZeile): string =>
    `Vorankuendigung_unterschrieben_${datumFuerDatei(f.createdAt)}.pdf`;

// ── Plot-Quelle (Plan §9, Entscheid E1) ─────────────────────────────────

export type VorankPlotQuelle =
    /** Keine unterschriebene Fassung: der Editorstand, ohne Rückfrage (E1). */
    | { art: 'stand' }
    /** Unterschriebene Fassung mit genau diesem Inhalt: sie wird geplottet. */
    | { art: 'unterschrieben'; fassung: VorankFassungZeile }
    /** Unterschrieben ist nur ein älterer Stand: Rückfrage, welcher geplottet wird. */
    | { art: 'frage'; fassung: VorankFassungZeile };

/**
 * Was „An Plotter senden“ plottet. Zählt nur, was eine unterschriebene PDF
 * HAT (`vorankUnterschriebenQuelle`). `fassungen` null (nicht geladen oder
 * nicht verfügbar) → der Stand, wie bisher (fail-open).
 */
export const vorankPlotQuelle = (
    fassungen: readonly VorankFassungZeile[] | null | undefined,
    aktuellerHash: string | null | undefined,
): VorankPlotQuelle => {
    const unterschrieben = (fassungen || []).filter(f => vorankUnterschriebenQuelle(f) !== null);
    const hash = (aktuellerHash || '').trim();
    const passend = hash ? juengste(unterschrieben.filter(f => f.inhaltHash === hash)) : null;
    if (passend) return { art: 'unterschrieben', fassung: passend };
    const aeltere = juengste(unterschrieben);
    if (aeltere) return { art: 'frage', fassung: aeltere };
    return { art: 'stand' };
};

/**
 * Der eingefrorene Stand des Plots: bei der unterschriebenen Fassung deren
 * Inhalt — Archiv und Ablage nach dem Plot nehmen dann genau ihn —, sonst
 * der Editorstand beim Klick.
 */
export const vorankPlotStandFuer = (data: VorankuendigungData, fassung: VorankFassungZeile | null): VorankPlotStand =>
    fassung
        ? { snapshot: fassung.inhalt, quelle: 'unterschrieben', fassungId: fassung.id }
        : { snapshot: data, quelle: 'stand', fassungId: null };

/** Texte der Rückfrage „unterschrieben, aber seither geändert“. */
export const vorankPlotFrage = (f: VorankFassungZeile): {
    titel: string; text: string; unterschrieben: string; stand: string;
} => ({
    titel: 'Welche Fassung plotten?',
    text: `Unterschrieben ist die Fassung vom ${vorankTag(f.createdAt)}. Der Stand im Editor wurde seither geändert — `
        + 'für ihn liegt keine Unterschrift vor.',
    unterschrieben: `Unterschriebene Fassung vom ${vorankTag(f.createdAt)} plotten`,
    stand: 'Aktuellen Stand ohne Unterschrift plotten',
});

// ── Begleitblatt beim Export (Plan §5) ──────────────────────────────────

/** Was der Export mit dem Begleitblatt vorhat — VOR der Anlage der Fassung. */
export type VorankBegleitblattPlan =
    /** Nur-Ansicht, oder Haken aus ohne gespeichertes Projekt: Export wie bisher, ohne Begleitblatt und ohne Fassung. */
    | { art: 'keins' }
    /**
     * Haken aus: kein Begleitblatt, aber die Fassung entsteht trotzdem — kommt
     * die Vorankündigung auf Papier unterschrieben zurück, lässt sie sich nur
     * einer angelegten Fassung zuordnen.
     */
    | { art: 'nur_fassung'; projektId: string }
    /** Projekt nie gespeichert: Begleitblatt nur mit Papierweg — eine Fassung ließe sich nicht zuordnen. */
    | { art: 'ohne_projekt' }
    | { art: 'anlegen'; projektId: string };

export const vorankBegleitblattPlan = (a: {
    haken: boolean;
    isReadOnly: boolean;
    /** Belastbare Projekt-Id (`belastbareProjektId`) oder null. */
    projektId: string | null;
}): VorankBegleitblattPlan => {
    if (a.isReadOnly) return { art: 'keins' };
    if (!a.projektId) return a.haken ? { art: 'ohne_projekt' } : { art: 'keins' };
    if (!a.haken) return { art: 'nur_fassung', projektId: a.projektId };
    return { art: 'anlegen', projektId: a.projektId };
};

/**
 * Nach `legeFassungAn`: mit Link, ohne Link (keine Fassung) oder gar kein
 * Begleitblatt, weil genau dieser Stand schon unterschrieben ist — dem
 * Bauherrn ein „bitte unterschreiben“ für etwas Unterschriebenes zu schicken,
 * wäre falsch. `bekannt` ist die geladene Zeile zur Fassung (gleicher Inhalt
 * ergibt dieselbe Fassung, `neu: false`); sie liefert „Fassung vom …“.
 */
export type VorankBegleitblattLink =
    | { art: 'link'; link: { url: string; gueltigBis: string }; fassungVom: string }
    /** `abgelaufenAm`: dieselbe Fassung kam wieder, ihr Link gilt aber nicht mehr — kein toter QR-Code aufs Blatt. */
    | { art: 'ohne_link'; fassungVom: string; abgelaufenAm?: string }
    | { art: 'schon_unterschrieben'; fassungVom: string };

export const vorankBegleitblattLink = (a: {
    angelegt: VorankFassungAngelegt | null;
    bekannt: VorankFassungZeile | null;
    origin: string;
    pathname: string;
    jetzt: string;
}): VorankBegleitblattLink => {
    if (!a.angelegt) return { art: 'ohne_link', fassungVom: a.jetzt };
    const bekannt = a.bekannt && a.bekannt.id === a.angelegt.fassungId ? a.bekannt : null;
    // Was der Server bei der Anlage meldet, gilt vor der geladenen Liste: die
    // kann älter sein als eine Unterschrift, die inzwischen hereinkam.
    const fassungVom = a.angelegt.fassungVom || (!a.angelegt.neu && bekannt?.createdAt) || a.jetzt;
    if (a.angelegt.unterschrieben || bekannt?.unterschrift) return { art: 'schon_unterschrieben', fassungVom };
    const jetzt = alsDatum(a.jetzt);
    if (jetzt && vorankLinkAbgelaufen({ gueltigBis: a.angelegt.gueltigBis }, jetzt)) {
        return { art: 'ohne_link', fassungVom, abgelaufenAm: a.angelegt.gueltigBis };
    }
    return {
        art: 'link',
        link: { url: vorankLinkUrl(a.origin, a.pathname, a.angelegt.token), gueltigBis: a.angelegt.gueltigBis },
        fassungVom,
    };
};

/**
 * Während der Export PDF und Fassung vorbereitete, wurde das Projekt
 * gewechselt: dann wird NICHTS heruntergeladen, gebucht, archiviert oder
 * abgelegt — der Kern schriebe sonst den Stand des alten Projekts in das neue.
 */
export const VORANK_EXPORT_PROJEKT_GEWECHSELT =
    'Der Export wurde abgebrochen: das Projekt wurde gewechselt, während das PDF vorbereitet wurde. '
    + 'Nichts heruntergeladen, gebucht oder archiviert — bitte im Projekt erneut exportieren.';

/** Der Satz, wenn schon das PDF des Exports nicht entstand — dann ist nichts heruntergeladen und nichts gebucht. */
export const vorankExportPdfFehlerText = (detail: string): string =>
    `Fehler beim Exportieren der PDF${(detail || '').trim() ? ` (${detail.trim()})` : ''} — nichts heruntergeladen, nichts gebucht.`;

/** Ergebnis des Begleitblatts — für eine Zeile im Abschlussdialog. */
export type VorankBegleitblattErgebnis =
    | { art: 'keins' }
    | { art: 'mit_link'; gueltigBis: string; neu: boolean }
    | { art: 'ohne_link'; grund: 'kein_projekt' | 'nicht_verfuegbar' }
    | { art: 'ohne_link'; grund: 'abgelaufen'; abgelaufenAm: string }
    | { art: 'schon_unterschrieben'; fassungVom: string }
    /** Haken aus (`nur_fassung`): kein Begleitblatt, die Fassung für den Papierweg ließ sich aber nicht anlegen. */
    | { art: 'keine_fassung' }
    | { art: 'fehler'; detail: string };

export const vorankBegleitblattHinweis = (
    e: VorankBegleitblattErgebnis | null | undefined,
): { ton: 'info' | 'warnung'; text: string } | null => {
    if (!e) return null;
    switch (e.art) {
        case 'keins':
            return null;
        case 'mit_link':
            return {
                ton: 'info',
                text: `Begleitblatt mit Unterschrift-Link angehängt (gültig bis ${vorankTag(e.gueltigBis)})`
                    + (e.neu ? '.' : ' — derselbe Link wie beim letzten Export dieses Stands.'),
            };
        case 'ohne_link':
            if (e.grund === 'abgelaufen') {
                return {
                    ton: 'warnung',
                    text: `Begleitblatt ohne Unterschrift-Link angehängt: der Link dieses Stands ist am ${vorankTag(e.abgelaufenAm)} `
                        + 'abgelaufen, und derselbe Inhalt ergibt dieselbe Fassung. Der Bauherr kann auf Papier unterschreiben '
                        + 'und die Datei zurücksenden.',
                };
            }
            return {
                ton: 'warnung',
                text: e.grund === 'kein_projekt'
                    ? 'Begleitblatt ohne Unterschrift-Link angehängt: das Projekt ist noch nicht gespeichert. '
                        + 'Der Bauherr kann auf Papier unterschreiben und die Datei zurücksenden.'
                    : 'Begleitblatt ohne Unterschrift-Link angehängt: der Link ließ sich gerade nicht anlegen. '
                        + 'Der Bauherr kann auf Papier unterschreiben und die Datei zurücksenden; '
                        + 'ein erneuter Export versucht es noch einmal.',
            };
        case 'schon_unterschrieben':
            return {
                ton: 'info',
                text: `Dieser Stand ist bereits unterschrieben (Fassung vom ${vorankTag(e.fassungVom)}) — `
                    + 'kein Begleitblatt angehängt. Die unterschriebene PDF gibt es in der Karte „Unterschrift des Bauherrn“.',
            };
        case 'keine_fassung':
            return {
                ton: 'warnung',
                text: 'Die Fassung zu diesem Stand ließ sich gerade nicht anlegen. Kommt die Vorankündigung auf Papier '
                    + 'unterschrieben zurück, lässt sie sich erst nach einem erneuten Export diesem Stand zuordnen.',
            };
        case 'fehler':
            return {
                ton: 'warnung',
                text: `Das Begleitblatt ließ sich nicht anhängen (${e.detail}) — exportiert wurde die Vorankündigung ohne Begleitblatt.`,
            };
        default:
            return null;
    }
};

// ── Statuskarte (Plan §8, §9, E2, E3) ───────────────────────────────────

/** Wie die Unterschrift hereinkam — ein kurzer Satzanfang. */
export const vorankWegText = (u: VorankUnterschrift): string => {
    if (u.weg === 'papier') return 'Auf Papier unterschrieben (eingelesen)';
    return u.art === 'bild' ? 'Digital unterschrieben (Unterschrift als Bild hochgeladen)' : 'Digital unterschrieben';
};

/** Ob die Benachrichtigung an den Ersteller ankam (E3) — `null`, wenn es nichts zu sagen gibt. */
export const vorankMailZeile = (f: Pick<VorankFassungZeile, 'mailGesendetAm' | 'mailFehler'>): string | null => {
    if (f.mailGesendetAm) return `Mail an den Ersteller zugestellt (${vorankTagZeit(f.mailGesendetAm)}).`;
    if ((f.mailFehler || '').trim()) return 'Mail nicht zugestellt — der Hinweis steht in der Glocke.';
    return null;
};

export type VorankKartenTon = 'neutral' | 'warten' | 'korrektur' | 'ok' | 'veraltet';

export interface VorankKarte {
    titel: string;
    ton: VorankKartenTon;
    zeilen: string[];
    /** Wortlaut des Korrekturwunschs (E2), für einen eigenen Block. */
    zitat: string | null;
    /** Fassung, deren Link angeboten wird (kopieren, QR) — nicht unterschrieben, nicht ersetzt, nicht abgelaufen. */
    linkFassung: VorankFassungZeile | null;
    /** Fassung, deren unterschriebene PDF angeboten wird (für die Behörde). */
    pdfFassung: VorankFassungZeile | null;
}

/** Den Link einer Fassung anbieten? Nur solange er gilt. */
const linkGilt = (f: VorankFassungZeile | null, jetzt: Date): VorankFassungZeile | null =>
    f && !f.unterschrift && !f.ersetztAm && !vorankLinkAbgelaufen(f, jetzt) && (f.token || '').trim() ? f : null;

/** Zeilen zu einer unterschriebenen Fassung: wer, wann, wie; Mailstatus beim digitalen Weg. */
const unterschriftZeilen = (f: VorankFassungZeile): string[] => {
    const u = f.unterschrift;
    if (!u) return [];
    const zeilen = [
        `${vorankWegText(u)} von ${(u.name || '').trim() || 'ohne Namen'} ${vorankUnterschriebenAm(u)}.`,
        `Fassung vom ${vorankTag(f.createdAt)}.`,
    ];
    const mail = u.weg === 'digital' ? vorankMailZeile(f) : null;
    if (mail) zeilen.push(mail);
    return zeilen;
};

/** „Fassung vom … — Link gültig bis …“ bzw. „… abgelaufen“. */
const linkZeile = (f: VorankFassungZeile, jetzt: Date, wer = 'Fassung'): string =>
    vorankLinkAbgelaufen(f, jetzt)
        ? `${wer} vom ${vorankTag(f.createdAt)} — der Link ist am ${vorankTag(f.gueltigBis)} abgelaufen.`
        : `${wer} vom ${vorankTag(f.createdAt)} — Link gültig bis ${vorankTag(f.gueltigBis)}.`;

const ABWEICHUNG_SATZ = 'Der Stand im Editor weicht von dieser Fassung ab. Der nächste Export legt eine neue Fassung an; '
    + 'der Link hier wird dann ungültig.';

/**
 * Was die Statuskarte zeigt — zum Zustand aus `vorankKartenStatus` (Dienst):
 * Titel, Ton, Zeilen, und für welche Fassung Link bzw. unterschriebene PDF
 * angeboten werden. „Unterschrieben, aber seither geändert“ kann schon eine
 * neuere Fassung haben, die auf ihre Unterschrift wartet — dann nennt die
 * Karte sie und bietet IHREN Link an.
 */
export const vorankKarte = (a: {
    status: VorankKartenStatus;
    fassungen: readonly VorankFassungZeile[] | null | undefined;
    aktuellerHash: string | null | undefined;
    jetzt: Date;
}): VorankKarte => {
    const liste = a.fassungen || [];
    const hash = (a.aktuellerHash || '').trim();
    const unterschrieben = liste.filter(f => !!f.unterschrift);
    const offen = liste.filter(f => !f.unterschrift);
    const nichtErsetzt = offen.filter(f => !f.ersetztAm);
    const mitPdf = (f: VorankFassungZeile | null): VorankFassungZeile | null =>
        f && vorankUnterschriebenQuelle(f) !== null ? f : null;

    if (a.status === 'unterschrieben' || a.status === 'unterschrieben_veraltet') {
        const f = a.status === 'unterschrieben'
            ? juengste(unterschrieben.filter(x => !!hash && x.inhaltHash === hash)) ?? juengste(unterschrieben)
            : juengste(unterschrieben);
        if (f) {
            const zeilen = unterschriftZeilen(f);
            let linkFassung: VorankFassungZeile | null = null;
            if (a.status === 'unterschrieben_veraltet') {
                zeilen.push('Der Stand im Editor weicht von der unterschriebenen Fassung ab — für die geänderten Angaben '
                    + 'braucht es eine neue Unterschrift.');
                const neuer = juengste(nichtErsetzt.filter(x => zeitwert(x.createdAt) > zeitwert(f.createdAt)));
                if (neuer) {
                    zeilen.push(`Wartet auf Unterschrift: ${linkZeile(neuer, a.jetzt, 'neuere Fassung')}`);
                    if (hash && neuer.inhaltHash !== hash) zeilen.push(ABWEICHUNG_SATZ);
                    linkFassung = linkGilt(neuer, a.jetzt);
                } else {
                    zeilen.push('Erneut mit Begleitblatt exportieren — das legt eine neue Fassung mit neuem Link an.');
                }
            }
            return {
                titel: a.status === 'unterschrieben' ? 'Unterschrieben' : 'Unterschrieben — Stand seither geändert',
                ton: a.status === 'unterschrieben' ? 'ok' : 'veraltet',
                zeilen,
                zitat: null,
                linkFassung,
                pdfFassung: mitPdf(f),
            };
        }
    }

    if (a.status === 'korrektur' || a.status === 'offen') {
        const f = a.status === 'korrektur'
            ? juengste(liste)
            : juengste(nichtErsetzt) ?? juengste(offen);
        if (f) {
            const zeilen: string[] = [];
            let zitat: string | null = null;
            if (a.status === 'korrektur' && f.korrektur) {
                zeilen.push(`${(f.korrektur.name || '').trim() || 'Der Bauherr'} am ${vorankTagZeit(f.korrektur.am)}:`);
                zitat = (f.korrektur.text || '').trim() || null;
                const mail = vorankMailZeile(f);
                if (mail) zeilen.push(mail);
                zeilen.push('Bitte die Angaben anpassen und erneut mit Begleitblatt exportieren — das legt eine neue Fassung '
                    + 'mit neuem Link an; der bisherige Link wird ungültig. Unterschreiben bleibt bis dahin möglich.');
            }
            zeilen.push(linkZeile(f, a.jetzt));
            if (hash && f.inhaltHash !== hash) zeilen.push(ABWEICHUNG_SATZ);
            // Beim Korrekturwunsch kann eine ältere Fassung schon unterschrieben sein.
            const aelter = a.status === 'korrektur' ? juengste(unterschrieben) : null;
            if (aelter) zeilen.push(`Unterschrieben ist bisher die Fassung vom ${vorankTag(aelter.createdAt)}.`);
            return {
                titel: a.status === 'korrektur' ? 'Korrektur gewünscht' : 'Wartet auf Unterschrift',
                ton: a.status === 'korrektur' ? 'korrektur' : 'warten',
                zeilen,
                zitat,
                linkFassung: linkGilt(f, a.jetzt),
                pdfFassung: mitPdf(aelter),
            };
        }
    }

    return {
        titel: 'Noch keine Fassung zum Unterschreiben',
        ton: 'neutral',
        zeilen: [
            'Der PDF-Export legt eine Fassung an — mit Begleitblatt auch QR-Code und Link, über den der Bauherr digital unterschreibt.',
            'Kommt eine auf Papier unterschriebene Vorankündigung zurück: erst exportieren (legt die Fassung an), '
                + 'dann hier einlesen.',
        ],
        zitat: null,
        linkFassung: null,
        pdfFassung: null,
    };
};

// ── Ablage in „Dokumente“ (Plan §7, §8) ─────────────────────────────────

/** Was `legeVorankDateiAb` außer Projekt und Datei bekommt. */
export interface VorankDateiAblage {
    dateiname: string;
    titel: string;
    beschreibung: string;
    /** Stabil je Datei: derselbe Auftrag zweimal ergibt dieselbe Zeile, nicht zwei. */
    fassungRef: string;
    stand: string;
}

/** Digital unterschrieben, aber die PDF liegt noch nicht in „Dokumente“ (Plan §7). */
export const vorankDigitalOhneAblage = (fassungen: readonly VorankFassungZeile[] | null | undefined): VorankFassungZeile[] =>
    (fassungen || []).filter(f => f.unterschrift?.weg === 'digital' && !f.signiertDokumentId && vorankPdfUnterschrift(f) !== null);

export const vorankDigitalAblage = (f: VorankFassungZeile): VorankDateiAblage => {
    const u = f.unterschrift;
    const wer = (u?.name || '').trim() || 'ohne Namen';
    const bild = u?.art === 'bild' ? ' (Unterschrift als Bild hochgeladen)' : '';
    return {
        dateiname: `Vorankuendigung_unterschrieben_digital_${datumFuerDatei(f.createdAt)}.pdf`,
        titel: `Vorankündigung — unterschrieben (digital) — Fassung vom ${vorankTag(f.createdAt)}`,
        beschreibung: `Digital unterschrieben von ${wer} ${vorankUnterschriebenAm(u)} über den Link …${vorankKennung(f.token)}${bild}. `
            + 'Von der App erzeugt, ohne Begleitblatt — die Fassung für Behörde und Aushang.',
        fassungRef: `vorank-unterschrieben-${f.id}`,
        stand: vorankTag(f.createdAt),
    };
};

/** Der Satz, wenn die automatische Ablage einer digital unterschriebenen Fassung scheiterte. */
export const vorankDigitalAblageFehler = (f: VorankFassungZeile, detail: string): string =>
    `Die digital unterschriebene Fassung vom ${vorankTag(f.createdAt)} ließ sich nicht in Dokumente ablegen (${detail}).`;

const ERLAUBTE_ENDUNGEN = ['.pdf', '.jpg', '.jpeg', '.png'];

const endungVon = (name: string | null | undefined): string => {
    const n = (name || '').trim().toLowerCase();
    const punkt = n.lastIndexOf('.');
    const endung = punkt >= 0 ? n.slice(punkt) : '';
    return ERLAUBTE_ENDUNGEN.includes(endung) ? endung : '';
};

const ENDUNG_JE_TYP: Record<string, string> = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
};

/** Endung der Originaldatei: aus dem Namen, sonst aus dem Typ, sonst `.pdf`. */
const originalEndung = (name: string | null | undefined, typ: string | null | undefined): string =>
    endungVon(name) || ENDUNG_JE_TYP[(typ || '').trim().toLowerCase()] || '.pdf';

/**
 * Ablage beim Papierweg: `original` (die Datei, wie sie kam — bei mehreren
 * Dateien entfällt sie, dann gilt die normalisierte PDF auch als Original)
 * und `signiert` (auf A4 gebracht, Quelle für Plot und Behörde).
 * `schluessel` kommt aus `vorankEinleseSchluessel`: dieselbe Eingabe noch
 * einmal eingelesen trifft dieselbe Zeile, eine andere Auswahl eine neue.
 */
export const vorankPapierAblage = (a: {
    fassung: VorankFassungZeile;
    art: 'original' | 'signiert';
    originalName: string | null;
    /** MIME-Typ der Originaldatei — für die Endung, wenn der Name keine brauchbare hat. */
    originalTyp?: string | null;
    schluessel: string;
    name: string | null;
    jetzt: string;
}): VorankDateiAblage => {
    const d = datumFuerDatei(a.fassung.createdAt);
    const vom = vorankTag(a.fassung.createdAt);
    const wer = (a.name || '').trim();
    const unterschrieben = wer ? ` Unterschrieben von ${wer}.` : '';
    if (a.art === 'original') {
        return {
            dateiname: `Vorankuendigung_unterschrieben_Original_${d}${originalEndung(a.originalName, a.originalTyp)}`,
            titel: `Vorankündigung — unterschrieben, Originaldatei — Fassung vom ${vom}`,
            beschreibung: `Die unterschriebene Datei, wie sie eingegangen ist (${(a.originalName || '').trim() || 'ohne Namen'}); `
                + `eingelesen am ${vorankTagZeit(a.jetzt)}.${unterschrieben}`,
            fassungRef: `vorank-papier-original-${a.fassung.id}-${a.schluessel}`,
            stand: vom,
        };
    }
    return {
        dateiname: `Vorankuendigung_unterschrieben_${d}.pdf`,
        titel: `Vorankündigung — unterschrieben (Papier) — Fassung vom ${vom}`,
        beschreibung: `Eingelesen am ${vorankTagZeit(a.jetzt)} und auf A4 gebracht — die Fassung für Behörde und Aushang.${unterschrieben}`,
        fassungRef: `vorank-papier-signiert-${a.fassung.id}-${a.schluessel}`,
        stand: vom,
    };
};

// ── Einlese-Dialog (Plan §8) ────────────────────────────────────────────

/** `accept` des Datei-Felds. */
export const VORANK_EINLESEN_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const ERLAUBTE_TYPEN = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

/** Prüft eine gewählte Datei VOR dem Lesen — Grund oder `null`. */
export const vorankPruefeEinleseDatei = (d: { name?: string; type?: string; size?: number } | null | undefined): string | null => {
    if (!d) return 'Bitte eine Datei wählen.';
    const name = (d.name || '').trim() || 'Die Datei';
    const groesse = d.size ?? 0;
    if (groesse <= 0) return `${name}: die Datei ist leer.`;
    if (groesse > VORANK_EINLESEN_MAX_BYTES) {
        return `${name} ist zu groß (höchstens ${Math.round(VORANK_EINLESEN_MAX_BYTES / 1024 / 1024)} MB).`;
    }
    const typ = (d.type || '').toLowerCase();
    if (ERLAUBTE_TYPEN.includes(typ) || (!typ && endungVon(d.name))) return null;
    if (typ.includes('heic') || typ.includes('heif') || /\.(heic|heif)$/i.test(d.name || '')) {
        return `${name}: HEIC-Fotos bitte als JPG oder PNG speichern.`;
    }
    return `${name}: nur PDF, JPG oder PNG.`;
};

/** Alle Gründe gegen eine Auswahl — leer = alles gut. */
export const vorankPruefeEinleseDateien = (
    dateien: readonly { name?: string; type?: string; size?: number }[],
): string[] => dateien.map(d => vorankPruefeEinleseDatei(d)).filter((g): g is string => !!g);

/** Fassungen, denen eine eingelesene Unterschrift gelten kann: noch nicht unterschrieben, neueste zuerst. */
export const vorankEinleseKandidaten = (fassungen: readonly VorankFassungZeile[] | null | undefined): VorankFassungZeile[] =>
    neuesteZuerst((fassungen || []).filter(f => !f.unterschrift));

/** Vorgabe der Zuordnung: die jüngste nicht unterschriebene, die nicht ersetzt ist — sonst die jüngste. */
export const vorankEinleseVorgabe = (kandidaten: readonly VorankFassungZeile[]): string | null =>
    (kandidaten.find(f => !f.ersetztAm) ?? kandidaten[0] ?? null)?.id ?? null;

/** Zustand einer Fassung in einem Wort (Auswahlliste des Dialogs). */
export const vorankFassungZustand = (f: VorankFassungZeile, jetzt: Date): string => {
    if (f.unterschrift) return 'unterschrieben';
    if (f.ersetztAm) return 'ersetzt';
    if (vorankLinkAbgelaufen(f, jetzt)) return 'Link abgelaufen';
    if (f.korrektur) return 'Korrektur gewünscht';
    return 'offen';
};

/** Eintrag der Auswahlliste: „Fassung vom …, HH:MM Uhr · 2 Seiten — offen“. */
export const vorankFassungOption = (f: VorankFassungZeile, jetzt: Date): string =>
    `Fassung vom ${vorankTagZeit(f.createdAt)} · ${f.seiten} ${f.seiten === 1 ? 'Seite' : 'Seiten'} — ${vorankFassungZustand(f, jetzt)}`;

/** Eine gewählte Datei mit ihren Seiten — in der Reihenfolge, in der sie eingelesen wird. */
export interface VorankEinleseEintrag<F> {
    id: string;
    datei: F;
    seiten: VorankEinleseSeite[];
}

export interface VorankSeitenWahl {
    mitnehmen: boolean;
    drehung: VorankDrehung;
}

export type VorankSeitenWahlen = Readonly<Record<string, VorankSeitenWahl>>;

export const vorankSeitenKey = (eintragId: string, seitenIndex: number): string => `${eintragId}#${seitenIndex}`;

/**
 * Einträge aus dem Ergebnis von `leseEinleseSeiten(dateien)` — `dateiIndex`
 * zählt dort in der übergebenen Liste. Die Ids nummerieren ab `ersteNummer`
 * weiter, damit sie über mehrere Hinzufügungen eindeutig bleiben.
 */
export const vorankNeueEintraege = <F>(
    dateien: readonly F[],
    seiten: readonly VorankEinleseSeite[],
    ersteNummer: number,
): VorankEinleseEintrag<F>[] =>
    dateien.map((datei, i) => ({
        id: `d${ersteNummer + i}`,
        datei,
        seiten: seiten.filter(s => s.dateiIndex === i).sort((x, y) => x.seitenIndex - y.seitenIndex),
    }));

/** Ein Element eine Stelle hoch (-1) oder runter (+1); am Rand unverändert. */
export const vorankVerschiebe = <T>(liste: readonly T[], index: number, richtung: -1 | 1): T[] => {
    const ziel = index + richtung;
    if (index < 0 || index >= liste.length || ziel < 0 || ziel >= liste.length) return [...liste];
    const neu = [...liste];
    [neu[index], neu[ziel]] = [neu[ziel], neu[index]];
    return neu;
};

export const vorankSeitenAnzahl = <F>(eintraege: readonly VorankEinleseEintrag<F>[]): number =>
    eintraege.reduce((n, e) => n + e.seiten.length, 0);

/**
 * Wahl je Seite. `vorauswahl` (aus `vorauswahlSeiten`) zählt in der
 * Anzeige-Reihenfolge. Ohne `ueberschreiben` behalten Seiten mit Wahl sie —
 * nur neu hinzugekommene bekommen die Vorauswahl; mit `ueberschreiben`
 * (andere Fassung gewählt) gilt die Vorauswahl für alle, die Drehung bleibt.
 * Neue Seiten starten mit der vorgeschlagenen Drehung (quer → 90°).
 */
export const vorankWahlMitVorauswahl = <F>(
    eintraege: readonly VorankEinleseEintrag<F>[],
    vorauswahl: readonly boolean[],
    bisher: VorankSeitenWahlen,
    ueberschreiben: boolean,
): Record<string, VorankSeitenWahl> => {
    const neu: Record<string, VorankSeitenWahl> = {};
    let position = 0;
    for (const e of eintraege) {
        for (const s of e.seiten) {
            const key = vorankSeitenKey(e.id, s.seitenIndex);
            const alt = bisher[key];
            neu[key] = alt && !ueberschreiben
                ? alt
                : { mitnehmen: vorauswahl[position] === true, drehung: alt?.drehung ?? s.drehungVorschlag ?? 0 };
            position += 1;
        }
    }
    return neu;
};

/** „drehen 90°“: im Uhrzeigersinn weiter. */
export const vorankDreheWeiter = (d: VorankDrehung): VorankDrehung => ((d + 90) % 360) as VorankDrehung;

/**
 * Was `normalisiereAufA4` bekommt: nur Dateien mit mindestens einer
 * mitgenommenen Seite, in der Reihenfolge der Anzeige; `dateiIndex` zählt in
 * genau dieser Liste, die Seiten stehen in derselben Reihenfolge.
 */
export const vorankEinleseAuftrag = <F>(
    eintraege: readonly VorankEinleseEintrag<F>[],
    wahl: VorankSeitenWahlen,
): { dateien: F[]; auswahl: VorankEinleseAuswahl } => {
    const dateien: F[] = [];
    const seiten: VorankEinleseAuswahl['seiten'] = [];
    for (const e of eintraege) {
        const mit = e.seiten.filter(s => wahl[vorankSeitenKey(e.id, s.seitenIndex)]?.mitnehmen);
        if (!mit.length) continue;
        const dateiIndex = dateien.length;
        dateien.push(e.datei);
        for (const s of mit) {
            seiten.push({ dateiIndex, seitenIndex: s.seitenIndex, drehung: wahl[vorankSeitenKey(e.id, s.seitenIndex)].drehung });
        }
    }
    return { dateien, auswahl: { seiten } };
};

/**
 * Kennung der Eingabe (Fassung, Dateien, Auswahl) für die `fassungRef` der
 * Ablage: ein zweiter Klick nach einem Fehler legt nichts doppelt ab, eine
 * andere Datei oder Auswahl ergibt eine neue Zeile — nie die alte Datei unter
 * neuem Namen.
 */
export const vorankEinleseSchluessel = (
    fassungId: string,
    dateien: readonly { name?: string; size?: number; lastModified?: number }[],
    auswahl: VorankEinleseAuswahl,
): string => stabilerInhaltsHash({
    f: fassungId,
    d: dateien.map(d => [d.name || '', d.size ?? 0, d.lastModified ?? 0]),
    s: auswahl.seiten.map(s => [s.dateiIndex, s.seitenIndex, s.drehung]),
});

/** Warum „Einlesen“ noch nicht geht — oder `null`. */
export const vorankEinleseSperre = (a: {
    fassung: VorankFassungZeile | null;
    seitenGesamt: number;
    mitgenommen: number;
    liest: boolean;
}): string | null => {
    if (a.liest) return 'Die Seiten werden gelesen …';
    if (!a.fassung) return 'Bitte die Fassung wählen, zu der die Unterschrift gehört.';
    if (a.seitenGesamt === 0) return 'Bitte die unterschriebene Datei wählen.';
    if (a.mitgenommen === 0) return 'Bitte mindestens eine Seite mitnehmen.';
    return null;
};

/** Hinweis, wenn die Zahl der mitgenommenen Seiten nicht zur Fassung passt — oder `null`. */
export const vorankSeitenHinweis = (mitgenommen: number, fassungSeiten: number): string | null => {
    if (!fassungSeiten || mitgenommen === 0 || mitgenommen === fassungSeiten) return null;
    return `Die Fassung hat ${fassungSeiten} ${fassungSeiten === 1 ? 'Seite' : 'Seiten'}, mitgenommen ${mitgenommen === 1 ? 'wird' : 'werden'} ${mitgenommen}. `
        + 'Das Begleitblatt gehört nicht dazu.';
};

/**
 * Der Satz nach einem Fehler, wenn schon etwas abgelegt ist: Abbrechen löscht
 * nichts, und ein zweites „Einlesen“ mit derselben Auswahl nimmt die
 * abgelegte Datei wieder (gleiche Kennung) statt sie zu verdoppeln.
 */
export const vorankTeilAbgelegtText = (titel: readonly string[]): string | null => {
    if (!titel.length) return null;
    const liste = titel.map(t => `„${t}“`).join(', ');
    return `Schon in Dokumente abgelegt: ${liste}. Das bleibt dort, auch beim Abbrechen; `
        + '„Einlesen“ mit derselben Auswahl verwendet es wieder, statt es doppelt abzulegen.';
};
